import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { classifyError } from '@/lib/fetchWithTimeout';

/**
 * Detects orphaned builder conversations (completed but never scored)
 * and offers the user a chance to recover them via a toast notification.
 * 
 * Mount in MainLayout so it runs once on app load for authenticated users.
 */
export function useOrphanedConversations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!user || checkedRef.current) return;
    checkedRef.current = true;

    const checkOrphans = async () => {
      try {
        const { data, error } = await supabase
          .from('builder_conversations')
          .select('id, final_prompt, completed_at')
          .eq('user_id', user.id)
          .eq('status', 'completed')
          .not('final_prompt', 'is', null)
          .is('submission_id', null)
          .gte('completed_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('completed_at', { ascending: false })
          .limit(1);

        if (error || !data || data.length === 0) return;

        const orphan = data[0];

        toast('You have an unscored prompt from a recent session.', {
          duration: 15000,
          action: {
            label: 'Submit now',
            onClick: async () => {
              const submitToast = toast.loading('Submitting your prompt...');
              try {
                const { data: result, error: scoreError } = await supabase.functions.invoke('score-submission', {
                  body: {
                    content: orphan.final_prompt,
                    submission_type: 'prompt_single',
                    source: 'app',
                    conversation_id: orphan.id,
                  },
                });

                if (scoreError) throw scoreError;
                if (result.error) throw new Error(result.error);

                toast.dismiss(submitToast);
                toast.success('Prompt scored successfully!');

                const transformedResult = {
                  ui: {
                    ...result.analysis,
                    score_id: result.score_id,
                    submission_id: result.submission_id,
                    is_public: !result.sensitivity_flag,
                    original_score: result.original_score,
                    improved_score: result.overall_score,
                    user_id: user.id,
                  },
                  telemetry: {
                    tags: result.analysis?.tags || [],
                  },
                };

                navigate(`/prompt_score/${result.score_id}`, { state: { result: transformedResult } });
              } catch (err) {
                toast.dismiss(submitToast);
                const classified = classifyError(err);
                toast.error(classified.message);
              }
            },
          },
        });
      } catch (err) {
        console.error('[OrphanDetection] Error checking orphaned conversations:', err);
      }
    };

    // Small delay so it doesn't compete with initial page load
    const timer = setTimeout(checkOrphans, 3000);
    return () => clearTimeout(timer);
  }, [user, navigate]);
}
